import { Controller } from '@hotwired/stimulus';
import {
  setDisplayBlockByIds,
  submitSelectedStudentChoiceCourses,
  showFinishCardContentAndSection,
  switchTwoColPaneToFinishSectionIfNeeded,
  getDusFromDom,
  normalizeNodeUid,
} from '../../utils/case_experience/case_experience_in_rails';
import { getCaseExperienceAdminPreviewMode } from '../../utils/case_experience/case_experience_api';
import { trapFocus, releaseFocusTrap } from '../../utils/FocusTrap';
import {
  hideLowTimeCaseModal,
  persistLowTimeModalPending,
  restoreLowTimeModalIfPending,
} from '../../utils/case_experience/low_time_case_modal';
import {
  hideCaseCompletionModal,
  persistCaseCompletionModalPending,
  restoreCaseCompletionModalIfPending,
} from '../../utils/case_experience/case_completion_modal';
import { armCaseCompletionModalAutocloseIfNeeded } from '../../utils/case_experience/completion_modals';
import { disableRailsuiEscape, showRailsuiModal } from '../../utils/railsui_modal';


// Connects to data-controller="case-experience--finish-case-card"

export default class extends Controller {
  static values = {
    documentId: Number,
    userId: Number,
    url: String,
    caseName: String,
    timeOnCase: String,
  };

  static targets = [
    'lowTimeModal',
    'completionModal',
    'confirmButton',
    'studentChoiceSubmit',
    'studentChoiceAcknowledge',
    'studentChoiceCheckbox',
  ];

  initialize() {
    this.enableConfirmHandler =
      this.enableConfirmButton.bind(this);
  }

  connect() {
    this.enableTakeQuizButtons();
    this.setupRadioListeners();
    this.setupIntersectionObserver();
    this.enableConfirmButton();
    if (this.hasLowTimeModalTarget) {
      disableRailsuiEscape(this.lowTimeModalTarget);
    }
    if (this.hasCompletionModalTarget) {
      disableRailsuiEscape(this.completionModalTarget);
    }
    void this.restorePendingCompletionModals();
  }

  async restorePendingCompletionModals() {
    // Only the finish-modals root owns modal targets; section mounts skip restore.
    if (!this.hasLowTimeModalTarget && !this.hasCompletionModalTarget) return;

    if (await restoreLowTimeModalIfPending()) return;
    if (await restoreCaseCompletionModalIfPending()) {
      armCaseCompletionModalAutocloseIfNeeded();
    }
  }


  disconnect() {
    this.removeRadioListeners();

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  setupRadioListeners() {
    const radios = this.element.querySelectorAll(
      'input[type="radio"][name="action"]',
    );

    radios.forEach((radio) => {
      radio.addEventListener(
        'change',
        this.enableConfirmHandler,
      );
    });
  }

  removeRadioListeners() {
    const radios = this.element.querySelectorAll(
      'input[type="radio"][name="action"]',
    );

    radios.forEach((radio) => {
      radio.removeEventListener(
        'change',
        this.enableConfirmHandler,
      );
    });
  }

  enableConfirmButton() {
    if (!this.hasConfirmButtonTarget) return;

    const checked = this.element.querySelector(
      'input[type="radio"][name="action"]:checked',
    );

    this.confirmButtonTarget.disabled = !checked;
  }

  async submitSelectedStudentChoiceCoursesForSection(event, lowTime = false) {
    const selectedCourseCheckboxes = this.studentChoiceCheckboxTargets || [];
    const selectedCourseIds = Array.from(selectedCourseCheckboxes)
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);

    if (selectedCourseIds.length === 0 || !this.documentIdValue) {
      return true;
    }

    if (lowTime && this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.setAttribute('aria-busy', 'true');
      this.confirmButtonTarget.disabled = true;
    }

    try {
      await submitSelectedStudentChoiceCourses(this.documentIdValue, selectedCourseIds);
    } catch (error) {
      window.alert('Failed to add as student-choice case. Please try again.');
      if (this.hasConfirmButtonTarget) {
        this.confirmButtonTarget.removeAttribute('aria-busy');
        this.confirmButtonTarget.disabled = false;
      }
      event?.preventDefault?.();
      return false;
    }

    if (lowTime && this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.removeAttribute('aria-busy');
      this.confirmButtonTarget.disabled = false;
    }

    return true;
  }

  async acknowledgeLowTime(event) {
    event.preventDefault();
    const submitted = await this.submitSelectedStudentChoiceCoursesForSection(event, true);
    if (!submitted) return;

    this.showFinishCard();
  }

  async resetCaseFromLowTime(event) {
    event.preventDefault();
    const submitted = await this.submitSelectedStudentChoiceCoursesForSection(event, true);
    if (!submitted) return;

    this.triggerHiddenResetCase();
  }

  async handleConfirm(event) {
    const submitted = await this.submitSelectedStudentChoiceCoursesForSection(event, true);
    if (!submitted) return;

    const acknowledgeRadio = this.element.querySelector(
      'input[name="action"][value="acknowledgeLowTime"]',
    );
    const resetCaseRadio = this.element.querySelector(
      'input[name="action"][value="resetCase"]',
    );

    if (acknowledgeRadio?.checked) {
      this.showFinishCard();
      event.preventDefault();
      return;
    }

    if (resetCaseRadio?.checked) {
      this.triggerHiddenResetCase();
      event.preventDefault();
    }
  }

  triggerHiddenResetCase() {
    const resetBtn = this.element.querySelector(
      '[data-case-experience--reset-case-target="resetCaseButton"]',
    );
    resetBtn?.click();
  }

  async showFinishCard() {
    hideLowTimeCaseModal();
    persistLowTimeModalPending(false);
    hideCaseCompletionModal();
    persistCaseCompletionModalPending(false);

    await showFinishCardContentAndSection();
  }

  adminCasePreviewActive() {
    return getCaseExperienceAdminPreviewMode() != null;
  }

  setupIntersectionObserver() {
    if (!this.element.classList.contains('finish_case_card')) return;
    if (this.adminCasePreviewActive()) return;

    this._observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.onVisible();
          }
        });
      },
      { threshold: 0.1 },
    );

    this._observer.observe(this.element);
  }

  finishCaseCardUid() {
    if (!this.element.classList.contains('finish_case_card')) return null;
    const rawUid = this.element.dataset?.uid;
    if (!rawUid) return null;
    return normalizeNodeUid(rawUid);
  }

  finishCaseCardRevealedInDus(dus, uid) {
    const entry = dus?.[uid];
    if (!entry) return false;
    return entry.invisible?.value === false && entry.collapsed?.value === false;
  }

  async onVisible() {
    if (this._finishCardIntersectionHandled) return;
    if (this.adminCasePreviewActive()) return;

    const uid = this.finishCaseCardUid();
    if (!uid) return;

    const dus = getDusFromDom();
    if (!this.finishCaseCardRevealedInDus(dus, uid)) return;

    this._finishCardIntersectionHandled = true;
    this._observer?.unobserve(this.element);

    setDisplayBlockByIds(['end_of_case_text']);
  }

  enableTakeQuizButtons() {
    this.element
      .querySelectorAll("[data-action*='case-experience--finish-case-card#finish']")
      .forEach((button) => {
        button.disabled = false;
      });
  }

  // --- Quiz AJAX Button logic ---
  async finish(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.disabled = false;
    if (button.dataset.quizModalLoading === "true") return;

    button.dataset.quizModalLoading = "true";
    button.setAttribute("aria-busy", "true");
    const loader = document.createElement("span");
    loader.className = "finish-card-loader";
    loader.setAttribute("aria-hidden", "true");
    loader.textContent = "Loading...";
    button.appendChild(loader);

    let hadError = false;
    try {
      const url = button.getAttribute("data-case-experience--finish-case-card-url-value") || this.urlValue;
      const caseName = button.getAttribute("data-case-experience--finish-case-card-case-name-value") || this.caseNameValue;
      const timeOnCase = button.getAttribute("data-case-experience--finish-case-card-time-on-case-value") || this.timeOnCaseValue;
      const modalRoot = document.getElementById("openQuestionContainer");
      const designSystemModal = this.designSystemQuizModal(modalRoot);
      const headers = { Accept: "text/vnd.turbo-stream.html" };
      if (designSystemModal) headers["X-Case-Experience-Quiz-Modal"] = "1";

      const response = await fetch(url, { headers, credentials: "same-origin" });
      if (!response.ok) {
        hadError = true;
        console.log(`Failed to load quiz: ${response.status} ${response.statusText}`);
        return;
      }
      const html = await response.text();
      if (window.Turbo && Turbo.renderStreamMessage) {
        await Turbo.renderStreamMessage(html);
      }

      if (designSystemModal) {
        this.syncQuizModalPayload();
        const shown = await showRailsuiModal(modalRoot);
        if (!shown) hadError = true;
      } else if (modalRoot) {
        this.revealLegacyQuizModal(modalRoot);
      } else {
        hadError = true;
      }

      if (!hadError && window.analytics && typeof window.analytics.track === "function") {
        window.analytics.track("open_questions", {
          page: window.location.href,
          source: "Case Page",
          case_name: caseName,
          time_on_case: timeOnCase
        });
      }
    } catch (e) {
      hadError = true;
      throw e;
    } finally {
      button.querySelector(".finish-card-loader")?.remove();
      button.removeAttribute("aria-busy");
      button.disabled = false;
      delete button.dataset.quizModalLoading;
      if (hadError) button.focus();
    }
  }

  designSystemQuizModal(modalRoot) {
    return modalRoot?.getAttribute("data-controller")?.split(/\s+/).includes("railsui-modal") === true;
  }

  syncQuizModalPayload() {
    const payload = document.querySelector("[data-case-experience-quiz-modal-payload]");
    if (!payload) return;

    const quizIdInput = document.querySelector(
      "[data-aquifer-ui-practice-smart-questions-modal-target='quizId']",
    );
    const quizName = document.querySelector(
      "[data-aquifer-ui-practice-smart-questions-modal-target='quizName']",
    );
    if (quizIdInput) quizIdInput.value = payload.dataset.quizId || "";
    if (quizName) quizName.textContent = payload.dataset.quizName || "";
  }

  revealLegacyQuizModal(modalRoot) {
    modalRoot.classList.remove("hidden");
    releaseFocusTrap(modalRoot);
    trapFocus(modalRoot);
    const obs = new MutationObserver(() => {
      if (modalRoot.classList.contains("hidden")) {
        releaseFocusTrap(modalRoot);
        obs.disconnect();
        if (document.getElementById("case-experience-active-pane")) {
          void switchTwoColPaneToFinishSectionIfNeeded();
        }
      }
    });
    obs.observe(modalRoot, { attributes: true, attributeFilter: ["class"] });
  }

  updateStudentChoiceButton = () => {
    // Find all checkboxes with the target
    if (!this.hasStudentChoiceSubmitTarget) return;
    const checkboxes = this.studentChoiceCheckboxTargets || [];
    const anyChecked = checkboxes.some(cb => cb.checked);
    this.studentChoiceSubmitTarget.disabled = !anyChecked;
  }

  async studentChoiceAcknowledge(event) {
    event.preventDefault();
    persistCaseCompletionModalPending(false);
    await showFinishCardContentAndSection();
  }

  async studentChoiceSubmit(event) {
    const submitted = await this.submitSelectedStudentChoiceCoursesForSection(event, false);
    if (!submitted) return;

    persistCaseCompletionModalPending(false);
    await showFinishCardContentAndSection();
  }
}
